import React, { ChangeEvent } from 'react';

import { Col, Form, Input, Radio, Row } from 'antd';

import { TContactType } from '../schemas/duplicateShipmentFormSchema';
import CountrySelector from '@/components/forms/CountrySelector';

interface IDuplicateShipmentAddressFormProps {
  prefix: string;
  values: Record<string, string>;
  errors: Record<string, string | undefined>;
  touched: Record<string, boolean | undefined>;
  handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  setFieldValue: (field: string, value: string) => void;
  showPhone: boolean;
}

export const DuplicateShipmentAddressForm = ({
  prefix,
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  showPhone,
}: IDuplicateShipmentAddressFormProps) => {
  const contactType = values[`${prefix}ContactType`] as TContactType;
  const isProfessional = contactType === 'professional';
  const nameRequired = !isProfessional;

  const field = (name: string) => `${prefix}${name}`;

  const getValidateStatus = (name: string) =>
    touched[field(name)] && errors[field(name)]
      ? ('error' as const)
      : undefined;

  const getHelp = (name: string) =>
    touched[field(name)] ? errors[field(name)] : undefined;

  const handleContactTypeChange = (value: TContactType) => {
    setFieldValue(field('ContactType'), value);

    if (value === 'personal') {
      setFieldValue(field('Company'), '');
    }
  };

  const nameFields = (
    <>
      <Form.Item
        label={`First name${nameRequired ? ' *' : ' (optional)'}`}
        validateStatus={getValidateStatus('FirstName')}
        help={getHelp('FirstName')}
      >
        <Input
          name={field('FirstName')}
          value={values[field('FirstName')]}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </Form.Item>
      <Form.Item
        label={`Last name${nameRequired ? ' *' : ' (optional)'}`}
        validateStatus={getValidateStatus('LastName')}
        help={getHelp('LastName')}
      >
        <Input
          name={field('LastName')}
          value={values[field('LastName')]}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </Form.Item>
    </>
  );

  return (
    <Form layout="vertical">
      <Radio.Group
        value={contactType}
        onChange={(e) =>
          handleContactTypeChange(e.target.value as TContactType)
        }
        style={{ marginBottom: '1rem' }}
      >
        <Radio.Button value="professional">Professional</Radio.Button>
        <Radio.Button value="personal">Personal</Radio.Button>
      </Radio.Group>

      {!isProfessional && nameFields}

      {isProfessional && (
        <Form.Item
          label="Company name *"
          validateStatus={getValidateStatus('Company')}
          help={getHelp('Company')}
        >
          <Input
            name={field('Company')}
            value={values[field('Company')]}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </Form.Item>
      )}

      <Form.Item
        label="Address *"
        validateStatus={getValidateStatus('Street')}
        help={getHelp('Street')}
      >
        <Input
          name={field('Street')}
          value={values[field('Street')]}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="City / Town *"
            validateStatus={getValidateStatus('City')}
            help={getHelp('City')}
          >
            <Input
              name={field('City')}
              value={values[field('City')]}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Zipcode *"
            validateStatus={getValidateStatus('PostalCode')}
            help={getHelp('PostalCode')}
          >
            <Input
              name={field('PostalCode')}
              value={values[field('PostalCode')]}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </Form.Item>
        </Col>
      </Row>

      <CountrySelector
        label="Country"
        countryCodeValue={values[field('CountryCode')]}
        onChange={(value: string) => setFieldValue(field('CountryCode'), value)}
      />

      {isProfessional && nameFields}

      {showPhone && (
        <Form.Item
          label="Phone number"
          validateStatus={getValidateStatus('PhoneNumber')}
          help={getHelp('PhoneNumber')}
        >
          <Input
            name={field('PhoneNumber')}
            value={values[field('PhoneNumber')]}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </Form.Item>
      )}

      <Form.Item
        label="Notification email *"
        validateStatus={getValidateStatus('Email')}
        help={getHelp('Email')}
      >
        <Input
          name={field('Email')}
          value={values[field('Email')]}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </Form.Item>
    </Form>
  );
};
